import React, { useEffect, useState } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { 
  Button,
  Container,
  Typography,
  Box,
  Alert,
  CircularProgress
} from '@mui/material';

import SportVlaanderenHeader from '../Components/common/Headers';
import { useRegistration } from '../hooks/useRegistration';
import { populateTagOptionsSupabase } from '../Utils/TagUtils';
import { loadUserByToken } from '../Utils/UserUtils';
import { fetchVenueData } from '../Utils/VenueUtils';

// Import reusable components
import PersonalInfoFields from '../Components/registration/PersonalInfoFields';
import MyLapsTagField from '../Components/registration/MyLapsTagField';
import SessionSelector from '../Components/registration/SessionSelector';
import UserPreferencesFields from '../Components/registration/UserPreferencesFields';
import EditLinkDialog from '../Components/registration/EditLinkDialog';

// Export for other files to import
export { populateTagOptionsSupabase };

/**
 * Registration form component for athlete registration
 * Handles form submission and validation using React Hook Form
 */
const RegistrationForm = () => {
  const [venueData, setVenueData] = useState(null);

  // Get edit token from URL params
  const { editToken } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  
  // Extract venue_id from URL parameters
  const params = new URLSearchParams(location.search);
  const venueId = params.get('venue_id') ? parseInt(params.get('venue_id')) : (location.pathname.includes('/register/gent') ? 1 : null);

  // console.log("Venue ID from URL when rendering RegistrationForm", venueId);

  // Effect to update URL with venue_id from user data
  useEffect(() => {
    if (editToken) {
      const loadUserData = async () => {
        try {
          // First, load the user data directly
          const { user, error } = await loadUserByToken(editToken);
          if (error) {
            console.error("Error loading user data:", error);
            return;
          }
          
          if (user && user.venue_id) {
            // Always force the venue_id in the URL to match the user's venue_id
            const currentParams = new URLSearchParams(location.search);
            const urlVenueId = currentParams.get('venue_id');
            
            // Only update if necessary
            if (urlVenueId !== user.venue_id.toString()) {
              console.log(`Updating venue_id in URL from ${urlVenueId} to ${user.venue_id}`);
              currentParams.set('venue_id', user.venue_id);
              
              // Use replace to avoid adding to browser history
              navigate(`${location.pathname}?${currentParams.toString()}`, { replace: true });
            }
          }
        } catch (error) {
          console.error("Error in loadUserData:", error);
        }
      };
      
      // Execute immediately
      loadUserData();
    }
  }, [editToken, location.pathname, navigate]);

  useEffect(() => {
    const loadVenueData = async () => {
      if (venueId) {
        const data = await fetchVenueData(venueId);
        if (data) {
          setVenueData(data);
        }
      }
    }

    loadVenueData();
  }, [venueId]);

  // Use our custom hook for form handling
  const {
    // Form methods
    register,
    handleSubmit,
    formState: { errors },
    control,
    
    // Form state
    loading,
    submitError,
    submitSuccess,
    
    // User preferences
    optInStats,
    setOptInStats,
    optInVideo,
    setOptInVideo,
    optInSocials,
    setOptInSocials,
    
    // Sessions and tags
    sessions,
    selectedSession,
    setSelectedSession,
    tagOptions,
    
    // Edit mode
    isUpdate,
    editLink,
    showEditLinkDialog,
    
    // Handlers
    handleReset,
    copyToClipboard,
    handleCloseEditLinkDialog
  } = useRegistration({ editToken, venueId });

  return (
    <>
      <SportVlaanderenHeader venueData={venueData} />

      <Container maxWidth="sm">
        <Box sx={{ my: 4 }}>
          <Typography variant="h4" component="h1" gutterBottom sx={{ textAlign: 'center' }}>
            Registratie MyLaps Transponder
          </Typography>

          {isUpdate && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Je werkt een bestaande registratie bij
            </Alert>
          )}

          {submitError && (<Alert severity="error" sx={{ mb: 2 }}>{submitError}</Alert>)}
          {submitSuccess && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {isUpdate ? 'Registratie bijgewerkt!' : 'Registratie gelukt!'}
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {/* Personal Information Fields */}
              <PersonalInfoFields register={register} errors={errors} />
              
              {/* MyLaps Tag Field */}
              <MyLapsTagField control={control} tagOptions={tagOptions} />
              
              {/* Session Selector */}
              <SessionSelector 
                sessions={sessions} 
                selectedSession={selectedSession} 
                onChange={setSelectedSession} 
              />
              
              {/* User Preferences Fields */}
              <UserPreferencesFields 
                optInStats={optInStats}
                setOptInStats={setOptInStats}
                optInVideo={optInVideo}
                setOptInVideo={setOptInVideo}
                optInSocials={optInSocials}
                setOptInSocials={setOptInSocials}
                register={register}
              />
              
              {/* Asterisk indicates required fields */}
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                * Verplicht in te vullen
              </Typography>

              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
                <Button
                  type="button"
                  variant="outlined"
                  onClick={handleReset}
                >
                  {isUpdate ? 'Annuleren' : 'Reset'}
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={loading}
                >
                  {loading ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : (
                    isUpdate ? 'Bijwerken' : 'Registreer'
                  )}
                </Button>
              </Box>
            </Box>
          </form>
        </Box>

        {/* Edit Link Dialog */}
        <EditLinkDialog 
          open={showEditLinkDialog}
          onClose={handleCloseEditLinkDialog}
          editLink={editLink}
          onCopy={copyToClipboard}
          isUpdate={isUpdate}
        />
      </Container>
    </>
  );
};

export default RegistrationForm;